import {useDataTracking} from '#imports';
import {
    type EditionOpenedEntryPoint,
    EditionOpenedLabel,
    type EditionOpenedSection,
    type EditionOpenedWorkflow,
    type InfluenceLinkType,
    type PortfolioEditionStepName,
    type PortfolioEditorElementType,
    type PortfolioErrorReason,
    type PortfolioInspirationEntryPoint,
    type PortfolioItemClickedEntryPoint,
    type PortfolioItemStatus,
    type PortfolioLinkRedirectionDecision,
    type PortfolioLinkRedirectionEntryPoint,
    type PortfolioProductTourStepName,
    type ProfileSectionReachedContext,
    type ProfileSectionReachedEntryPoint,
    type ProfileSectionReachedSection,
    ProfileUpdatedElementStatus,
    ProfileUpdatedSection,
    type ProfileViewedOrigin,
    ProfileViewMode,
    type RecommendationDeletedBy,
    type RecommendationEmailLinkOpenedRecommenderStatus,
    type RecommendationRequestStartedEntryPoint,
    type SocialShareEntryPoint,
    TrackingEvents,
    type TranslationAction,
} from '~/config/TrackingEvents';
import type {CompletionStatus} from '~/features/experience/store/experiencesEditStore';
import type {Profile} from '~/types/domain/Profile';

export function useProfileTracking() {
    const {track} = useDataTracking();

    function trackProfileViewed(
        searchId: string,
        advancedSearchId: string,
        sourcingHubCatalogId: string,
        accountId: string | undefined,
        origin: ProfileViewedOrigin | undefined,
        mode: ProfileViewMode,
        isOwner: boolean,
        microConsultingSearchId?: string,
    ) {
        track(TrackingEvents.PROFILE_VIEWED, {
            search_event_id: searchId,
            advanced_search_id: advancedSearchId,
            catalog_id: sourcingHubCatalogId,
            freelancer_account_id: accountId,
            origin,
            mode,
            is_owner: isOwner,
            micro_consulting_search_id: microConsultingSearchId,
        });
    }

    function trackProfileEdition(
        section: EditionOpenedSection,
        entryPoint: EditionOpenedEntryPoint,
        workflow: EditionOpenedWorkflow,
        existingElement: boolean,
        label?: EditionOpenedLabel,
    ) {
        track(TrackingEvents.PROFILE_EDITION_OPENED, {
            profile_section: section,
            entry_point: entryPoint,
            workflow,
            existing_element: existingElement,
            label,
        });
    }

    function trackProfileTranslated(profile: Profile, section: string, outputLanguage: string, action: TranslationAction) {
        track(TrackingEvents.TRANSLATION, {
            profile_section: section,
            input_language: profile.dominantLanguageDetected,
            output_language: outputLanguage,
            action,
            freelancer_account_id: profile.accountId,
        });
    }

    function trackProfileUpdateCardViewed(section: EditionOpenedSection, label?: EditionOpenedLabel) {
        track(TrackingEvents.PROFILE_UPDATE_CARD_VIEWED, {
            profile_section: section,
            label,
        });
    }

    function trackProfileSocialShareStarted(socialNetwork: SocialShareEntryPoint, accountId: string, isOwner: boolean = false) {
        track(TrackingEvents.PROFILE_SOCIAL_SHARE_STARTED, {
            entry_point: socialNetwork,
            freelancer_account_id: accountId,
            is_owner: isOwner,
        });
    }

    function trackPortfolioSocialShareOpened(portfolioItemId: string | undefined) {
        track(TrackingEvents.PROFILE_SOCIAL_SHARE_OPENED, {
            portfolio_item_id: portfolioItemId,
        });
    }

    function trackPortfolioSocialShareStarted(network: SocialShareEntryPoint, portfolioItemId: string | undefined) {
        track(TrackingEvents.PROFILE_SOCIAL_SHARE_STARTED, {
            entry_point: network,
            portfolio_item_id: portfolioItemId,
        });
    }

    function trackProfileSocialShareImageDownloaded(accountId: string) {
        track(TrackingEvents.PROFILE_SOCIAL_SHARE_IMAGE_DOWNLOADED, {
            freelancer_account_id: accountId,
        });
    }

    function trackProfileSocialShareOpened(accountId: string, isOwner: boolean = false) {
        track(TrackingEvents.PROFILE_SOCIAL_SHARE_OPENED, {
            freelancer_account_id: accountId,
            is_owner: isOwner,
        });
    }

    function trackProfileUpdated(section: ProfileUpdatedSection, existingElement: boolean) {
        track(TrackingEvents.PROFILE_UPDATED, {
            profile_section: section,
            existing_element: existingElement,
            element_status: existingElement ? ProfileUpdatedElementStatus.UPDATED : ProfileUpdatedElementStatus.CREATED,
        });
    }

    function trackProfileSectionReached(
        section: ProfileSectionReachedSection,
        entryPoint: ProfileSectionReachedEntryPoint,
        profileId: string | undefined,
        freelancerAccountId: string | undefined,
        context: ProfileSectionReachedContext,
    ) {
        track(TrackingEvents.PROFILE_SECTION_REACHED, {
            section: section,
            entry_point: entryPoint,
            profile_id: profileId,
            freelancer_account_id: freelancerAccountId,
            context: context,
        });
    }

    function trackInfluenceLinkClicked(linkType: InfluenceLinkType, profileId: string) {
        track(TrackingEvents.INFLUENCE_LINK_CLICKED, {
            link_type: linkType,
            profile_id: profileId,
        });
    }

    function trackRecommendationRequestStarted(profileId: string, entryPoint: RecommendationRequestStartedEntryPoint) {
        track(TrackingEvents.RECOMMENDATION_REQUEST_STARTED, {
            profile_id: profileId,
            entry_point: entryPoint,
        });
    }

    function trackRecommendationLeft(experienceId: string | undefined, profileId: string) {
        track(TrackingEvents.RECOMMENDATION_LEFT, {
            experience_id: experienceId,
            profile_id: profileId,
        });
    }

    function trackRecommendationDeleted(profileId: string, deletedBy: RecommendationDeletedBy, experienceId: string | undefined) {
        track(TrackingEvents.RECOMMENDATION_DELETED, {
            profile_id: profileId,
            deleted_by: deletedBy,
            experience_id: experienceId,
        });
    }

    function trackRecommendationEmailLinkOpened(profileId: string, recommenderStatus: RecommendationEmailLinkOpenedRecommenderStatus) {
        track(TrackingEvents.RECOMMENDATION_EMAIL_LINK_OPENED, {
            profile_id: profileId,
            recommender_status: recommenderStatus,
        });
    }

    function trackAskProfileRecommendationSent(
        profileId: string,
        entryPoint: RecommendationRequestStartedEntryPoint | undefined,
        numberEmailsAddedToInvitation: number,
        experienceId: string | null,
    ) {
        track(TrackingEvents.RECOMMENDATION_REQUEST_SENT, {
            profile_id: profileId,
            recommendation_entrypoint: entryPoint,
            number_emails_added_to_invitation: numberEmailsAddedToInvitation,
            experience_id: experienceId,
        });
    }

    function trackPortfolioEditorElementAdded(portfolioItemId: string | undefined, elementType: PortfolioEditorElementType) {
        track(TrackingEvents.PORTFOLIO_EDITOR_ELEMENT_ADDED, {
            portfolio_item_id: portfolioItemId,
            element_type: elementType,
        });
    }

    function trackPortfolioErrorDisplayed(portfolioItemId: string | undefined, errorReason: PortfolioErrorReason) {
        track(TrackingEvents.PORTFOLIO_ERROR_DISPLAYED, {
            portfolio_item_id: portfolioItemId,
            error_reason: errorReason,
        });
    }

    function trackPortfolioExit(portfolioItemId: string | undefined) {
        track(TrackingEvents.PORTFOLIO_EXIT, {
            portfolio_item_id: portfolioItemId,
        });
    }

    function trackPortfolioDraftSaved(portfolioItemId: string | undefined) {
        track(TrackingEvents.PORTFOLIO_DRAFT_SAVED, {
            portfolio_item_id: portfolioItemId,
        });
    }

    function trackPortfolioPublished(
        portfolioItemId: string,
        category: string | undefined,
        hasThumbnail: boolean,
        hasSkills: boolean,
        hasIndustry: boolean,
    ) {
        track(TrackingEvents.PROFILE_UPDATED, {
            profile_section: ProfileUpdatedSection.PORTFOLIO,
            portfolio_item_id: portfolioItemId,
            category,
            has_thumbnail: hasThumbnail,
            has_skills: hasSkills,
            has_industry: hasIndustry,
        });
    }

    function trackPortfolioEditionStepViewed(portfolioItemId: string | undefined, stepName: PortfolioEditionStepName) {
        track(TrackingEvents.PORTFOLIO_EDITION_STEP_VIEWED, {
            existing_element: !!portfolioItemId,
            step_name: stepName,
        });
    }

    function trackPortfolioReordered() {
        track(TrackingEvents.PORTFOLIO_REORDERED);
    }

    function trackPortfolioDeleted(portfolioItemId: string, status: PortfolioItemStatus) {
        track(TrackingEvents.PORTFOLIO_DELETED, {
            portfolio_item_id: portfolioItemId,
            item_status: status,
        });
    }

    function trackProfilePortfolioSectionClicked(profileId: string, nbElementsViewed: number) {
        track(TrackingEvents.PROFILE_PORTFOLIO_SECTION_CLICKED, {
            profile_id: profileId,
            nb_elements_viewed: nbElementsViewed,
        });
    }

    function trackPortfolioItemClicked(portfolioItemId: string, entryPoint: PortfolioItemClickedEntryPoint) {
        track(TrackingEvents.PROFILE_PORTFOLIO_SECTION_CLICKED, {
            portfolio_item_id: portfolioItemId,
            entry_point: entryPoint,
        });
    }

    function trackPortfolioProductTourStepViewed(stepName: PortfolioProductTourStepName) {
        track(TrackingEvents.TEMP_NEW_PORTFOLIO_PRODUCT_TOUR_STEP_VIEWED, {
            step_name: stepName,
        });
    }

    function trackPortfolioInspirationViewed(entryPoint: PortfolioInspirationEntryPoint) {
        track(TrackingEvents.PORTFOLIO_INSPIRATION_VIEWED, {
            entry_point: entryPoint,
        });
    }

    function trackPortfolioInspirationClicked(portfolioItemId: string) {
        track(TrackingEvents.PORTFOLIO_INSPIRATION_CLICKED, {
            portfolio_item_id: portfolioItemId,
        });
    }

    function trackPortfolioLinkRedirection(decision: PortfolioLinkRedirectionDecision, entryPoint: PortfolioLinkRedirectionEntryPoint) {
        track(TrackingEvents.PROFILE_PORTFOLIO_LINK_REDIRECTION, {
            decision,
            entry_point: entryPoint,
        });
    }

    function compareCopiedTextWithFreelancerName(freelancerName: string) {
        if (import.meta.client) {
            const getSelection = window.getSelection || document.getSelection;
            const selection = !getSelection ? '' : getSelection()?.toString() || '';
            if (!!selection && freelancerName.includes(selection)) {
                track('freelancer_name_selection_in_profile_page');
            }
        }
    }

    function toEditionOpenedLabel(label: CompletionStatus): EditionOpenedLabel {
        switch (label) {
            case 'INVALID':
                return EditionOpenedLabel.INVALID;
            case 'INCOMPLETE':
                return EditionOpenedLabel.MANDATORY_INFORMATION_MISSING;
            case 'CAN_BE_ENHANCED':
                return EditionOpenedLabel.CAN_BE_ENHANCED;
            case 'ALL_SET':
                return EditionOpenedLabel.WELL_DONE;
        }
    }

    function trackWishListOpened(freelancerAccountId: string | undefined, source: string = 'profile_page') {
        return track('client_favorite_heart_started', {freelancer_account_id: freelancerAccountId, source});
    }

    function trackFreelancerAddedToWishList(freelancerAccountId: string | undefined, source: string = 'profile_page') {
        return track('client_favorite_saved', {freelancer_account_id: freelancerAccountId, source, action: 'added'});
    }

    function trackFreelancerRemovedFromWishList(freelancerAccountId: string | undefined, source: string = 'profile_page') {
        return track('client_favorite_saved', {freelancer_account_id: freelancerAccountId, source, action: 'removed'});
    }

    return {
        trackAskProfileRecommendationSent,
        trackInfluenceLinkClicked,
        trackProfileEdition,
        trackPortfolioEditorElementAdded,
        trackPortfolioEditionStepViewed,
        trackPortfolioSocialShareOpened,
        trackPortfolioSocialShareStarted,
        trackPortfolioErrorDisplayed,
        trackPortfolioExit,
        trackPortfolioDraftSaved,
        trackPortfolioPublished,
        trackPortfolioReordered,
        trackPortfolioDeleted,
        trackProfilePortfolioSectionClicked,
        trackPortfolioItemClicked,
        trackPortfolioProductTourStepViewed,
        trackPortfolioInspirationViewed,
        trackPortfolioInspirationClicked,
        trackPortfolioLinkRedirection,
        trackProfileSectionReached,
        trackProfileTranslated,
        trackProfileUpdateCardViewed,
        trackProfileSocialShareStarted,
        trackProfileSocialShareOpened,
        trackProfileSocialShareImageDownloaded,
        trackProfileUpdated,
        trackProfileViewed,
        trackRecommendationRequestStarted,
        trackRecommendationLeft,
        trackRecommendationDeleted,
        trackRecommendationEmailLinkOpened,
        compareCopiedTextWithFreelancerName,
        toEditionOpenedLabel,
        trackWishListOpened,
        trackFreelancerAddedToWishList,
        trackFreelancerRemovedFromWishList,
    };
}
